/**
 * EnjoySignatureCRUD — K1 · Admin Catalogue · Enjoy Signature.
 *
 * Founder ruling 14 Jun 2026 PM later still (post-M1 sign-off · K1 = Enjoy
 * Signature first):
 *   "K1 may build list/read/create/edit/patch/activate/deactivate/archive/
 *   restore/clone/publish-with-validation/local-image-upload/preview/
 *   replace/remove/audit/role-permissions/publish-gate-validation-reuse.
 *   Default delete behaviour should be archive/soft delete. Hard delete
 *   deferred to a separate proposal."
 *
 * Two views:
 *   · list view (default · grid of rows · status chips · search/filter)
 *   · detail view (when a row is selected · edit form + image uploader +
 *     lifecycle buttons + audit trail)
 *
 * The UI is clean enough for desk use — not a debug panel.
 *
 * Backend endpoints used:
 *   GET    /api/os-admin/catalogue/enjoy-signature
 *   GET    /api/os-admin/catalogue/enjoy-signature/{id}
 *   POST   /api/os-admin/catalogue/enjoy-signature
 *   PATCH  /api/os-admin/catalogue/enjoy-signature/{id}     (If-Match: "<version>")
 *   POST   /api/os-admin/catalogue/enjoy-signature/{id}/activate
 *   POST   /api/os-admin/catalogue/enjoy-signature/{id}/deactivate
 *   POST   /api/os-admin/catalogue/enjoy-signature/{id}/archive
 *   POST   /api/os-admin/catalogue/enjoy-signature/{id}/restore
 *   POST   /api/os-admin/catalogue/enjoy-signature/{id}/publish
 *   POST   /api/os-admin/catalogue/enjoy-signature/{id}/clone
 *   POST   /api/os-admin/catalogue/enjoy-signature/{id}/image/upload
 *   DELETE /api/os-admin/catalogue/enjoy-signature/{id}/image
 *   GET    /api/os-admin/catalogue/enjoy-signature/{id}/audit
 */
import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import ImageUploader from "@/pages/admin/os/ImageUploader";

const ROOT = "/os-admin/catalogue/enjoy-signature";

const STATUS_CHIP = {
    draft: { bg: "#fef3c7", fg: "#78350f", label: "Draft" },
    live: { bg: "#dcfce7", fg: "#14532d", label: "Live" },
    published: { bg: "#dcfce7", fg: "#14532d", label: "Live" },
    archived: { bg: "#e5e7eb", fg: "#374151", label: "Archived" },
};


function StatusChip({ status }) {
    const c = STATUS_CHIP[status] || STATUS_CHIP.draft;
    return (
        <span
            data-testid={`k1-status-chip-${status}`}
            style={{
                background: c.bg, color: c.fg,
                padding: "2px 8px", borderRadius: 999,
                fontSize: 11, fontWeight: 600,
                letterSpacing: "0.05em", textTransform: "uppercase",
            }}
        >
            {c.label}
        </span>
    );
}


function ListView({ items, total, q, onSearchChange, statusFilter, onStatusChange, onOpen, onCreate, loading, flagDisabled }) {
    return (
        <div data-testid="k1-list-view">
            <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
                <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }} data-testid="k1-title">
                    Enjoy Signature · Catalogue
                </h1>
                <span style={{ color: "#666", fontSize: 13 }}>
                    {total} {total === 1 ? "row" : "rows"}
                </span>
                <div style={{ flex: 1 }} />
                <input
                    type="search"
                    placeholder="Search title / city / country"
                    value={q}
                    onChange={(e) => onSearchChange(e.target.value)}
                    data-testid="k1-search-input"
                    style={{ padding: "6px 10px", border: "1px solid #ccc", borderRadius: 4, minWidth: 240 }}
                />
                <select
                    value={statusFilter}
                    onChange={(e) => onStatusChange(e.target.value)}
                    data-testid="k1-status-filter"
                    style={{ padding: "6px 10px", border: "1px solid #ccc", borderRadius: 4 }}
                >
                    <option value="">All statuses</option>
                    <option value="draft">Draft</option>
                    <option value="live">Live</option>
                    <option value="archived">Archived</option>
                </select>
                <button
                    type="button"
                    onClick={onCreate}
                    data-testid="k1-create-btn"
                    style={{
                        padding: "8px 14px", borderRadius: 4,
                        background: "#1f2d3d", color: "white", border: 0,
                        cursor: "pointer", fontWeight: 600,
                    }}
                >
                    + New Enjoy Signature
                </button>
            </div>

            {flagDisabled ? (
                <div data-testid="k1-flag-disabled-banner" style={{
                    padding: 14, borderRadius: 6, background: "#fef3c7", color: "#78350f",
                    border: "1px solid #fde68a", marginBottom: 16,
                }}>
                    Admin Catalogue (K1 · Enjoy Signature) is currently <strong>disabled</strong> for this environment.
                    Set <code>CATALOGUE_K1_ENABLED=true</code> in the backend env and restart to enable.
                </div>
            ) : null}

            {loading ? (
                <div style={{ color: "#666" }} data-testid="k1-list-loading">Loading…</div>
            ) : items.length === 0 ? (
                <div style={{ color: "#666", padding: 20, textAlign: "center" }} data-testid="k1-list-empty">
                    No signatures match the current filter.
                </div>
            ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
                    {items.map((it) => (
                        <div
                            key={it.id}
                            onClick={() => onOpen(it.id)}
                            data-testid={`k1-row-${it.id}`}
                            style={{
                                border: "1px solid #e5e7eb", borderRadius: 8,
                                background: "white", color: "#1a2332", cursor: "pointer",
                                transition: "box-shadow 100ms",
                                overflow: "hidden", display: "flex", flexDirection: "column",
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)")}
                            onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "")}
                        >
                            <div style={{
                                aspectRatio: "16/10", background: "#f3f4f6",
                                display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                                {it.image_url ? (
                                    <img src={it.image_url} alt={it.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                ) : (
                                    <div style={{ color: "#999", fontSize: 12 }}>No image</div>
                                )}
                            </div>
                            <div style={{ padding: 12, flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                                <div style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.3 }} title={it.title}>
                                    {it.title || <i style={{ color: "#999" }}>Untitled</i>}
                                </div>
                                <div style={{ color: "#666", fontSize: 12 }}>
                                    {[it.category, it.city, it.country].filter(Boolean).join(" · ")}
                                </div>
                                <div style={{ flex: 1 }} />
                                <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "space-between" }}>
                                    <StatusChip status={it.publish_status} />
                                    <span style={{ fontSize: 11, color: "#999" }}>v{it.version}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}


function DetailView({ rowId, onBack, onChanged }) {
    const [row, setRow] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [audit, setAudit] = useState([]);
    const [showAudit, setShowAudit] = useState(false);
    const [draft, setDraft] = useState({});
    const [dirty, setDirty] = useState(false);

    const refresh = useCallback(async () => {
        try {
            const r = await api.get(`${ROOT}/${rowId}`);
            setRow(r.data);
            setDraft({
                title: r.data.title || "",
                category: r.data.category || "",
                city: r.data.city || "",
                country: r.data.country || "",
                concierge_whisper: r.data.concierge_whisper || "",
                member_facing_notes: r.data.member_facing_notes || "",
                indicative_price: r.data.indicative_price || "",
                vat_mention: r.data.vat_mention || "",
                member_badge: r.data.member_badge || "",
                best_for: r.data.best_for || "",
            });
            setDirty(false);
            setError(null);
        } catch (e) {
            setError(formatApiError(e?.response?.data?.detail) || e.message);
        }
    }, [rowId]);

    useEffect(() => { refresh(); }, [refresh]);

    const loadAudit = useCallback(async () => {
        try {
            const r = await api.get(`${ROOT}/${rowId}/audit?limit=50`);
            setAudit(r.data.items || []);
        } catch (e) {
            // Audit may be 403 for read-only · don't block UI
        }
    }, [rowId]);

    useEffect(() => { if (showAudit) loadAudit(); }, [showAudit, loadAudit]);

    const handleField = (k, v) => {
        setDraft((d) => ({ ...d, [k]: v }));
        setDirty(true);
    };

    const handleSave = async () => {
        if (!row) return;
        setBusy(true);
        setError(null);
        try {
            const patch = {};
            Object.entries(draft).forEach(([k, v]) => {
                if ((row[k] || "") !== (v || "")) patch[k] = v;
            });
            if (Object.keys(patch).length === 0) {
                setDirty(false);
                setBusy(false);
                return;
            }
            await api.patch(`${ROOT}/${rowId}`, patch, {
                headers: { "If-Match": `"${row.version}"` },
            });
            await refresh();
            onChanged?.();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.error === "version_conflict") {
                setError("Row was edited by another operator. Refresh to see the latest, then re-apply your changes.");
            } else if (detail?.error === "validation_failed") {
                setError("Validation failed: " + (detail.violations || []).join(" · "));
            } else {
                setError(formatApiError(detail) || e.message);
            }
        } finally {
            setBusy(false);
        }
    };

    const callAction = async (action, label) => {
        if (!row) return;
        setBusy(true);
        setError(null);
        try {
            await api.post(`${ROOT}/${rowId}/${action}`);
            await refresh();
            onChanged?.();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.error === "publish_blocked") {
                setError(`Publish blocked: ${(detail.blocks || []).join(" · ")}`);
            } else {
                setError(formatApiError(detail) || e.message || `${label} failed`);
            }
        } finally {
            setBusy(false);
        }
    };

    const handleImageUpload = async (file) => {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`${ROOT}/${rowId}/image/upload`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        await refresh();
        onChanged?.();
    };

    const handleImageRemove = async () => {
        await api.delete(`${ROOT}/${rowId}/image`);
        await refresh();
        onChanged?.();
    };

    if (!row) {
        return (
            <div data-testid="k1-detail-loading">
                <button onClick={onBack} data-testid="k1-detail-back-btn" style={{ marginBottom: 16 }}>
                    ← Back to list
                </button>
                {error ? <div style={{ color: "#a02020" }}>{error}</div> : <div>Loading…</div>}
            </div>
        );
    }

    return (
        <div data-testid="k1-detail-view">
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <button
                    onClick={onBack}
                    data-testid="k1-detail-back-btn"
                    style={{ padding: "6px 10px", border: "1px solid #ccc", borderRadius: 4, background: "white", cursor: "pointer" }}
                >
                    ← Back to list
                </button>
                <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, flex: 1 }} data-testid="k1-detail-title">
                    {row.title || <i>Untitled</i>}
                </h1>
                <StatusChip status={row.publish_status} />
                <span style={{ fontSize: 12, color: "#999" }}>v{row.version}</span>
            </div>

            {error ? (
                <div data-testid="k1-detail-error" style={{
                    padding: 10, marginBottom: 12, borderRadius: 4,
                    background: "#fdecea", color: "#a02020",
                }}>{error}</div>
            ) : null}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: 24 }}>
                {/* LEFT · the form */}
                <div data-testid="k1-form">
                    <Field label="Title" testid="title">
                        <input
                            type="text" value={draft.title || ""}
                            onChange={(e) => handleField("title", e.target.value)}
                            data-testid="k1-input-title"
                            style={inputStyle}
                        />
                    </Field>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                        <Field label="Category" testid="category">
                            <input
                                type="text" value={draft.category || ""}
                                onChange={(e) => handleField("category", e.target.value)}
                                data-testid="k1-input-category" style={inputStyle}
                            />
                        </Field>
                        <Field label="Member badge" testid="member_badge">
                            <input
                                type="text" value={draft.member_badge || ""}
                                onChange={(e) => handleField("member_badge", e.target.value)}
                                data-testid="k1-input-member-badge" style={inputStyle}
                            />
                        </Field>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                        <Field label="City" testid="city">
                            <input
                                type="text" value={draft.city || ""}
                                onChange={(e) => handleField("city", e.target.value)}
                                data-testid="k1-input-city" style={inputStyle}
                            />
                        </Field>
                        <Field label="Country" testid="country">
                            <input
                                type="text" value={draft.country || ""}
                                onChange={(e) => handleField("country", e.target.value)}
                                data-testid="k1-input-country" style={inputStyle}
                            />
                        </Field>
                    </div>
                    <Field label="Concierge whisper (member-facing · ≤ 280 chars)" testid="concierge_whisper">
                        <textarea
                            value={draft.concierge_whisper || ""}
                            maxLength={280}
                            onChange={(e) => handleField("concierge_whisper", e.target.value)}
                            data-testid="k1-input-concierge-whisper"
                            style={{ ...inputStyle, minHeight: 80, fontFamily: "inherit" }}
                        />
                        <div style={{ fontSize: 11, color: "#999", textAlign: "right" }}>
                            {(draft.concierge_whisper || "").length}/280
                        </div>
                    </Field>
                    <Field label="Member-facing notes (≤ 1200 chars)" testid="member_facing_notes">
                        <textarea
                            value={draft.member_facing_notes || ""}
                            maxLength={1200}
                            onChange={(e) => handleField("member_facing_notes", e.target.value)}
                            data-testid="k1-input-notes"
                            style={{ ...inputStyle, minHeight: 100, fontFamily: "inherit" }}
                        />
                    </Field>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                        <Field label="Indicative price" testid="indicative_price">
                            <input
                                type="text" value={draft.indicative_price || ""}
                                onChange={(e) => handleField("indicative_price", e.target.value)}
                                data-testid="k1-input-price" style={inputStyle}
                            />
                        </Field>
                        <Field label="VAT mention" testid="vat_mention">
                            <input
                                type="text" value={draft.vat_mention || ""}
                                onChange={(e) => handleField("vat_mention", e.target.value)}
                                data-testid="k1-input-vat" style={inputStyle}
                            />
                        </Field>
                        <Field label="Best for" testid="best_for">
                            <input
                                type="text" value={draft.best_for || ""}
                                onChange={(e) => handleField("best_for", e.target.value)}
                                data-testid="k1-input-best-for" style={inputStyle}
                            />
                        </Field>
                    </div>

                    <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                        <button
                            onClick={handleSave}
                            disabled={!dirty || busy}
                            data-testid="k1-save-btn"
                            style={{ ...btnStyle, ...primaryBtnStyle, opacity: !dirty || busy ? 0.5 : 1 }}
                        >
                            {busy ? "Saving…" : "Save changes"}
                        </button>
                        <button
                            onClick={() => callAction("publish", "Publish")}
                            disabled={busy}
                            data-testid="k1-publish-btn"
                            style={{ ...btnStyle, ...primaryBtnStyle }}
                        >
                            Publish
                        </button>
                        {row.publish_status === "live" || row.publish_status === "published" ? (
                            <button
                                onClick={() => callAction("deactivate", "Deactivate")}
                                disabled={busy} data-testid="k1-deactivate-btn"
                                style={btnStyle}
                            >Deactivate</button>
                        ) : null}
                        {row.publish_status !== "archived" ? (
                            <button
                                onClick={() => callAction("archive", "Archive")}
                                disabled={busy} data-testid="k1-archive-btn"
                                style={btnStyle}
                            >Archive</button>
                        ) : (
                            <button
                                onClick={() => callAction("restore", "Restore")}
                                disabled={busy} data-testid="k1-restore-btn"
                                style={btnStyle}
                            >Restore</button>
                        )}
                        <button
                            onClick={() => callAction("clone", "Clone")}
                            disabled={busy} data-testid="k1-clone-btn"
                            style={btnStyle}
                        >Clone</button>
                    </div>
                </div>

                {/* RIGHT · image uploader + audit toggle */}
                <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        Image
                    </div>
                    <ImageUploader
                        currentImageUrl={row.image_url}
                        currentDims={{ width: row.image_width, height: row.image_height }}
                        onUpload={handleImageUpload}
                        onRemove={row.image_url ? handleImageRemove : null}
                        disabled={busy}
                    />
                    <div style={{ marginTop: 16 }}>
                        <button
                            type="button"
                            onClick={() => setShowAudit((v) => !v)}
                            data-testid="k1-audit-toggle"
                            style={{ ...btnStyle, width: "100%" }}
                        >
                            {showAudit ? "Hide" : "Show"} audit trail
                        </button>
                        {showAudit ? (
                            <div data-testid="k1-audit-list" style={{ marginTop: 12, fontSize: 12, fontFamily: "ui-monospace, monospace" }}>
                                {audit.length === 0 ? <div style={{ color: "#999" }}>No audit rows yet</div> :
                                    audit.map((a, i) => (
                                        <div key={i} data-testid={`k1-audit-row-${a.event}`} style={{ marginBottom: 6, padding: 6, background: "#f9fafb", borderRadius: 4 }}>
                                            <div><strong>{a.event}</strong> · {a.actor} · {a.actor_role}</div>
                                            <div style={{ color: "#666" }}>{a.at}</div>
                                        </div>
                                    ))}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}


function Field({ label, testid, children }) {
    return (
        <div style={{ marginBottom: 12 }} data-testid={`k1-field-${testid}`}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
                {label}
            </label>
            {children}
        </div>
    );
}


const inputStyle = {
    width: "100%", padding: "8px 10px",
    border: "1px solid #d1d5db", borderRadius: 4,
    fontSize: 14, boxSizing: "border-box",
    background: "#fff", color: "#1a2332",
};
const btnStyle = {
    padding: "8px 14px", borderRadius: 4,
    border: "1px solid #ccc", background: "white",
    cursor: "pointer", fontSize: 14,
};
const primaryBtnStyle = {
    background: "#1f2d3d", color: "white", border: 0,
    fontWeight: 600,
};


function CreateModal({ onClose, onCreated }) {
    const [draft, setDraft] = useState({
        title: "", category: "", city: "", country: "",
        concierge_whisper: "",
    });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const handleCreate = async () => {
        if (!draft.title.trim()) {
            setError("Title is required.");
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const res = await api.post(ROOT, draft);
            onCreated(res.data);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.error === "validation_failed") {
                setError("Validation failed: " + (detail.violations || []).join(" · "));
            } else {
                setError(formatApiError(detail) || e.message);
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="k1-create-modal" style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
        }}>
            <div style={{ background: "white", borderRadius: 8, padding: 24, width: 480, maxWidth: "90vw" }}>
                <h2 style={{ marginTop: 0 }}>New Enjoy Signature</h2>
                <Field label="Title *" testid="create-title">
                    <input
                        type="text" autoFocus
                        value={draft.title}
                        onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                        data-testid="k1-create-input-title" style={inputStyle}
                    />
                </Field>
                <Field label="Category" testid="create-category">
                    <input
                        type="text" value={draft.category}
                        onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                        data-testid="k1-create-input-category" style={inputStyle}
                    />
                </Field>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <Field label="City" testid="create-city">
                        <input
                            type="text" value={draft.city}
                            onChange={(e) => setDraft({ ...draft, city: e.target.value })}
                            data-testid="k1-create-input-city" style={inputStyle}
                        />
                    </Field>
                    <Field label="Country" testid="create-country">
                        <input
                            type="text" value={draft.country}
                            onChange={(e) => setDraft({ ...draft, country: e.target.value })}
                            data-testid="k1-create-input-country" style={inputStyle}
                        />
                    </Field>
                </div>
                <Field label="Concierge whisper" testid="create-whisper">
                    <textarea
                        value={draft.concierge_whisper}
                        maxLength={280}
                        onChange={(e) => setDraft({ ...draft, concierge_whisper: e.target.value })}
                        data-testid="k1-create-input-whisper"
                        style={{ ...inputStyle, minHeight: 80, fontFamily: "inherit" }}
                    />
                </Field>
                {error ? <div style={{ color: "#a02020", marginBottom: 10, fontSize: 13 }}>{error}</div> : null}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                    <button onClick={onClose} data-testid="k1-create-cancel" style={btnStyle}>Cancel</button>
                    <button
                        onClick={handleCreate}
                        disabled={busy}
                        data-testid="k1-create-confirm"
                        style={{ ...btnStyle, ...primaryBtnStyle, opacity: busy ? 0.5 : 1 }}
                    >
                        {busy ? "Creating…" : "Create draft"}
                    </button>
                </div>
            </div>
        </div>
    );
}


export default function EnjoySignatureCRUD() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openId, setOpenId] = useState(null);
    const [showCreate, setShowCreate] = useState(false);
    const [flagDisabled, setFlagDisabled] = useState(false);

    const fetchList = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams();
            if (statusFilter) params.set("publish_status", statusFilter);
            if (q) params.set("q", q);
            params.set("limit", "100");
            const res = await api.get(`${ROOT}?${params}`);
            setItems(res.data.items || []);
            setTotal(res.data.total || 0);
            setFlagDisabled(false);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.error === "catalogue_k1_disabled") {
                setFlagDisabled(true);
                setItems([]);
                setTotal(0);
            } else {
                setError(formatApiError(detail) || e.message);
            }
        } finally {
            setLoading(false);
        }
    }, [q, statusFilter]);

    useEffect(() => {
        const t = setTimeout(() => fetchList(), 250);  // debounce search
        return () => clearTimeout(t);
    }, [fetchList]);

    if (openId) {
        return (
            <div className="os-light-surface">
                <DetailView
                    rowId={openId}
                    onBack={() => { setOpenId(null); fetchList(); }}
                    onChanged={fetchList}
                />
            </div>
        );
    }

    return (
        <div data-testid="k1-enjoy-signature-crud" className="os-light-surface" style={{ padding: 16 }}>
            <ListView
                items={items} total={total}
                q={q} onSearchChange={setQ}
                statusFilter={statusFilter} onStatusChange={setStatusFilter}
                onOpen={(id) => setOpenId(id)}
                onCreate={() => setShowCreate(true)}
                loading={loading}
                flagDisabled={flagDisabled}
            />
            {error ? (
                <div style={{ marginTop: 16, padding: 10, background: "#fdecea", color: "#a02020", borderRadius: 4 }} data-testid="k1-list-error">
                    {error}
                </div>
            ) : null}
            {showCreate ? (
                <CreateModal
                    onClose={() => setShowCreate(false)}
                    onCreated={(row) => {
                        setShowCreate(false);
                        setOpenId(row.id);
                    }}
                />
            ) : null}
        </div>
    );
}
